import { TrophyTwoTone } from '@ant-design/icons'
import { Button, Card, Checkbox, Col, List, Progress, Row, Space, Statistic, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { Layout } from 'antd'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import {
  ChecklistItem,
  communicationSkillsChecklistItems,
  emotionalIntelligenceChecklistItems,
  financialLiteracyChecklistItems,
  friendsAndFamilyChecklistItems,
  householdCareChecklistItems,
  hygieneChecklistItems,
  problemSolvingChecklistItems,
  responsibilityChecklistItems,
  spiritualAwarenessChecklistItems,
  timeManagementChecklistItems,
} from './adulting-101-checklists'

const { Title, Paragraph, Text } = Typography

interface QuizCategory {
  key: string
  label: string
  path: string
  items: ChecklistItem[]
}

const categories: QuizCategory[] = [
  { key: 'hygiene', label: 'Hygiene', path: '/adulting-101/hygiene', items: hygieneChecklistItems },
  { key: 'time-management', label: 'Time Management', path: '/adulting-101/time-management', items: timeManagementChecklistItems },
  {
    key: 'communication-skills',
    label: 'Communication Skills',
    path: '/adulting-101/communication-skills',
    items: communicationSkillsChecklistItems,
  },
  { key: 'responsibility', label: 'Responsibility', path: '/adulting-101/responsibility', items: responsibilityChecklistItems },
  {
    key: 'emotional-intelligence',
    label: 'Emotional Intelligence',
    path: '/adulting-101/emotional-intelligence',
    items: emotionalIntelligenceChecklistItems,
  },
  { key: 'problem-solving', label: 'Problem Solving', path: '/adulting-101/problem-solving', items: problemSolvingChecklistItems },
  {
    key: 'financial-literacy',
    label: 'Financial Literacy',
    path: '/adulting-101/financial-literacy',
    items: financialLiteracyChecklistItems,
  },
  { key: 'household-care', label: 'Household Care', path: '/adulting-101/household-care', items: householdCareChecklistItems },
  {
    key: 'friends-and-family',
    label: 'Friends and Family',
    path: '/adulting-101/friends-and-family',
    items: friendsAndFamilyChecklistItems,
  },
  // { key: 'like-love-lust', label: 'Like, Love, Lust', path: '/adulting-101/like-love-lust', items: likeLoveLustChecklistItems },
  {
    key: 'spiritual-awareness',
    label: 'Spiritual Awareness',
    path: '/adulting-101/spiritual-awareness',
    items: spiritualAwarenessChecklistItems,
  },
]

function itemKey(categoryKey: string, itemId: string) {
  return `${categoryKey}::${itemId}`
}

function getScoreColor(percent: number) {
  if (percent >= 80) return '#52c41a'
  if (percent >= 50) return '#faad14'
  return '#f5222d'
}

function getVerdict(percent: number) {
  if (percent >= 90) return 'You are a full-grown adult.'
  if (percent >= 80) return 'You are mostly an adult.'
  if (percent >= 70) return "You're well on your way to adulthood."
  if (percent >= 60) return 'You have some adulting habits, but there is room to grow.'
  if (percent >= 50) return 'You are starting to develop adulting habits.'
  if (percent >= 40) return 'You are beginning to learn adulting habits.'
  if (percent >= 30) return 'You are acting like a 3 year old.'
  return 'You really are acting like a baby.'
}

export function Adulting101QuizPage() {
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(() => new Set())
  const [submitted, setSubmitted] = useState(false)

  const totalItems = useMemo(() => categories.reduce((sum, category) => sum + category.items.length, 0), [])
  const totalChecked = checkedKeys.size
  const overallPercent = totalItems === 0 ? 0 : Math.round((totalChecked / totalItems) * 100)

  const categoryResults = useMemo(
    () =>
      categories.map((category) => {
        const checkedInCategory = category.items.filter((item) => checkedKeys.has(itemKey(category.key, item.id))).length
        const percent = category.items.length === 0 ? 0 : Math.round((checkedInCategory / category.items.length) * 100)
        return { ...category, checkedInCategory, percent }
      }),
    [checkedKeys]
  )

  function toggleItem(categoryKey: string, itemId: string, checked: boolean) {
    setCheckedKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys)
      const key = itemKey(categoryKey, itemId)
      if (checked) {
        nextKeys.add(key)
      } else {
        nextKeys.delete(key)
      }
      return nextKeys
    })
  }

  return (
    <Layout>
      <Header>
        <h1>Am I An Adult Yet?</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #fff0f6 0%, #ffffff 220px)' }}>
        <Title level={2}>Take the quiz to find out how "adult" you really are.</Title>
        <Paragraph type="secondary">
          Think about the last week. Check off everything you've actually been doing regularly across every area of adulting. As in, more
          often than not (70-80% of the time). When you're done, submit to see your total score and a breakdown by category. Be honest with
          yourself — this is just for you.
        </Paragraph>

        {!submitted ? (
          <>
            {categories.map((category) => (
              <Card key={category.key} title={category.label} style={{ marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <List
                  dataSource={category.items}
                  renderItem={(item) => (
                    <List.Item>
                      <Checkbox
                        checked={checkedKeys.has(itemKey(category.key, item.id))}
                        onChange={(event) => toggleItem(category.key, item.id, event.target.checked)}
                      >
                        {item.text}
                      </Checkbox>
                    </List.Item>
                  )}
                />
              </Card>
            ))}
            <Button type="primary" size="large" onClick={() => setSubmitted(true)} style={{ marginBottom: '40px' }}>
              See My Results
            </Button>
          </>
        ) : (
          <>
            <Card style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <TrophyTwoTone twoToneColor="#faad14" style={{ fontSize: '48px' }} />
                <Progress type="circle" percent={overallPercent} size={160} strokeColor={getScoreColor(overallPercent)} />
                <Statistic title="Items Completed" value={totalChecked} suffix={`/ ${totalItems}`} />
                <Title level={3}>{getVerdict(overallPercent)}</Title>
              </Space>
            </Card>

            <Card title="Score Breakdown by Area" style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Row gutter={[16, 16]}>
                {categoryResults.map((result) => (
                  <Col xs={24} sm={12} key={result.key}>
                    <div style={{ marginBottom: '4px' }}>
                      <Link to={result.path}>{result.label}</Link>
                      <Text type="secondary" style={{ float: 'right' }}>
                        {result.checkedInCategory} / {result.items.length}
                      </Text>
                    </div>
                    <Progress percent={result.percent} strokeColor={getScoreColor(result.percent)} />
                  </Col>
                ))}
              </Row>
            </Card>

            <Button onClick={() => setSubmitted(false)} style={{ marginBottom: '40px' }}>
              Retake the Quiz
            </Button>
          </>
        )}
      </Content>
    </Layout>
  )
}
